'use client'
import { useEffect } from 'react'

import { useParams, usePathname, useSearchParams } from 'next/navigation'
import Link from 'next/link'
import moment from 'moment'
import get from 'lodash/get'
import { Button } from '@nextui-org/button'
import { Skeleton } from '@nextui-org/react'

import { useAppDispatch, useAppSelector } from '@/hooks'
import { useCheckLogin } from '@/hooks/useCheckLogin'
import { useGetProfileQuery } from '@/redux/api/profile/profileW3'
import { authActions } from '@/redux/reducers/auth/auth.reducer'
import SearchHeadBar from '@/components/layouts/SearchHeadBar'
import ExperienceSidebar from '@/components/layouts/SideBar/ExperienceSidebar'
import SiderQC from '@/components/layouts/SideBar/SiderQC'
import Header from '@/components/layouts/Header'
import ModalWelcome from '@/components/modals/ModalWelcome'
import { useFCMToken } from '@/hooks/useFCMToken'
import NavigationComponents from '@/components/layouts/Navigation'
import { useUpdateCurrentTokenMutation } from '@/redux/api/notification'
import { cn } from '@/utils/cn'
import useIsMobile from '@/hooks/useIsMobile'
import InviteCode from '@/components/layouts/SideBar/components/InviteCode'
import { ModalSwitchNetwork } from '@/components/modals/ModalSwitchNetwork'
import { useGetStatisticsReferralQuery } from '@/redux/api/wallet/wallet.apiw3'

import styles from './styles.module.css'
import SideBar from '../../components/layouts/SideBar'

export default function GlobalLayout({ children }: { children: React.ReactNode }) {
  useCheckLogin()
  const { token } = useFCMToken()
  const user = useAppSelector((state) => state.root.auth.user?.id)
  const menu = useAppSelector((state) => state.root.settings.menu)
  const isMobile = useIsMobile()

  const { data, isLoading } = useGetStatisticsReferralQuery({})
  const bonus = data?.promotion.referralConfig.referralBonus || []
  const lastBonus = bonus.at(-1)

  // const walletAddress = useAppSelector((state) => state.root.auth.user?.walletAddress)
  const { data: profile } = useGetProfileQuery({ userId: user as string }, { skip: !user })
  const inviteCode = useSearchParams().get('inviteCode')
  const pathName = usePathname()
  const params = useParams()
  const dispatch = useAppDispatch()
  const disableSideBar: Record<string, boolean> = {
    [`/message`]: false,
    [`/message/${params.userID}`]: false,
    [`/kol`]: false,
    [`/kol/list`]: false,
    [`/events`]: false,
    [`/events/${params.type}`]: false,
    [`/events/${params.type}/${params.id}`]: false,
    [`/event-manager`]: false,
    ['/connect-social']: false,
    ['/my-space']: false,
    ['/wallet']: false,

    // ['/my-space/detail']: false,
  }
  const enableSideBarRight: string[] = [
    '/',
    '/feed',
    '/profile',
    `/profile/${params.userID}`,
    '/notifications',
    '/explore',
  ]

  const disableHeader = ['/spaces/create']
  const disableNavigation = ['/spaces/create']
  const spaceActive = useAppSelector((state) => state.root.settings.spaceActive)

  useEffect(() => {
    if (inviteCode) {
      dispatch(authActions.setInviteCode(inviteCode || ''))
    }
  }, [inviteCode])

  useEffect(() => {
    if (profile) {
      if (spaceActive) {
        const space = get(profile, 'spaces', []).find((o) => o.id === spaceActive)
        if (space) {
          dispatch(
            authActions.setUser({
              ...profile,
              spaceAvatarUrl: space.profileImageUrl,
              spaceNickname: space.name,
              nickname: space.name,
              avatarImage: space.profileImageUrl,
              avatarOrigin: profile.avatarImage,
              nicknameOrigin: profile.nickname,
            }),
          )
        }
      } else {
        dispatch(authActions.setUser(profile))
      }
    }
  }, [profile, spaceActive])

  useEffect(() => {
    if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/service-worker.js')
        .then((registration) => {
          console.log('Service Worker registered with scope:', registration.scope)
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error)
        })
    }
  }, [])

  const [updateCurrentToken] = useUpdateCurrentTokenMutation()
  useEffect(() => {
    if (token && !profile?.deviceToken) {
      updateCurrentToken(token)
    }
  }, [token, profile])

  return (
    <>
      <div
        className={cn('bg-ss-dark text-ss-grayscale-100', {
          // block scroll when toggle sidebar on mobile
          'overflow-hidden h-[100svh] max-h-[100svh]': menu && isMobile,
        })}
      >
        <div className={styles.containerGlobal}>
          <SideBar />

          <div className={styles.mainContent}>
            <main className={cn(styles.contentPage, disableNavigation.includes(pathName) ? '' : 'pb-20')}>
              <div className={disableSideBar[pathName] == undefined ? styles.contentLeft : styles.contentLeftMessage}>
                {!disableHeader.includes(pathName) && <Header />}

                {children}
              </div>
              {/*<NotifyFeatureModal />*/}
            </main>
          </div>

          {enableSideBarRight.includes(pathName) && (
            <div
              className="sticky right-0 top-0 hidden h-screen max-w-[354px] shrink-0 overflow-y-auto customScrollbar py-4 pl-8 xl:block pr-1"
              style={{ scrollbarGutter: 'stable' }}
            >
              <div className="flex flex-col gap-6">
                <SearchHeadBar />
                <div className="flex flex-col gap-4">
                  <ExperienceSidebar />
                  <div className="w-full p-4 bg-ss-primary/10 rounded-xl space-y-3">
                    <div className="space-y-2">
                      <div>Refer Friends Earn SightSea Point</div>
                      <div className="text-ss-grayscale-500 text-sm font-normal">
                        {!isLoading ? (
                          <>
                            When you reach {lastBonus?.milestone || 1000} referrals, you will receive{' '}
                            <span className="text-ss-accent-400">{lastBonus?.bonusPoints || 1500} SSP</span>.
                          </>
                        ) : (
                          <Skeleton className="h-5 w-full" />
                        )}
                      </div>

                      <div>
                        <Link href="/wallet?tab=referral&category=leaderboard">
                          <Button type="button" className="w-full bg-gradient-main">
                            Leaderboard
                          </Button>
                        </Link>
                      </div>
                    </div>
                    <InviteCode />
                  </div>
                  <SiderQC />
                  <div className="flex gap-1 justify-between text-xs text-ss-grayscale-600">
                    <Link href="/term-of-use" target="_blank">
                      <span>Terms of Service</span>
                    </Link>
                    <Link href="/privacy-policy" target="_blank">
                      <span>Privacy Policy</span>
                    </Link>
                    <span>©Sightsea {moment().format('YYYY')}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!disableNavigation.includes(pathName) && <NavigationComponents />}
        </div>
      </div>

      {profile && !profile?.hasCompletedTutorial && <ModalWelcome />}

      <ModalSwitchNetwork />
    </>
  )
}
